import classNames from "classnames";
import { FunctionComponent, JSX, PropsWithoutRef, SVGProps, useEffect, useMemo, useState } from "react";
import Marquee from "react-fast-marquee";
import styles from "./References.module.scss";
import LogoAmpcontrol from "./logo-ampcontrol.svg?react";
import LogoAnker from "./logo-anker.svg?react";
import LogoApple from "./logo-apple.svg?react";
import LogoAxa from "./logo-axa.svg?react";
import LogoCisco from "./logo-cisco.svg?react";
import LogoElectrolux from "./logo-electrolux.svg?react";
import LogoForm from "./logo-form.svg?react";
import LogoFortytwoStudio from "./logo-fortytwo-studio.svg?react";
import LogoMapfre from "./logo-mapfre.svg?react";
import LogoMicrosoft from "./logo-microsoft.svg?react";
import LogoMidmark from "./logo-midmark.svg?react";
import LogoMobbex from "./logo-mobbex.svg?react";
import LogoNovartis from "./logo-novartis.svg?react";
import LogoOpticks from "./logo-opticks.svg?react";
import LogoTheWallStreetJournal from "./logo-the-wall-street-journal.svg?react";
import LogoUnitedNations from "./logo-united-nations.svg?react";

export type ReferencesProps = Overwrite<Omit<PropsWithoutRef<JSX.IntrinsicElements["div"]>, "children">, {
  references?: Array<ReferenceTitle>;
}>;

type ReferenceTitle =
  "Anker" |
  "Apple" |
  "AXA" |
  "Ampcontrol" |
  "Cisco" |
  "Electrolux" |
  "Form" |
  "FortyTwo Studio" |
  "Mapfre" |
  "Microsoft" |
  "Midmark" |
  "Mobbex" |
  "Novartis" |
  "Opticks" |
  "The Wall Street Journal" |
  "United Nations";

type Reference = {
  Logo: FunctionComponent<SVGProps<SVGSVGElement>>;
  heightPercentage?: number;
  title: ReferenceTitle;
};

type State = {
  isReady: boolean;
};

const allReferences: Array<Reference> = [
  { Logo: LogoAmpcontrol, title: "Ampcontrol" },
  { Logo: LogoAnker, heightPercentage: 75, title: "Anker" },
  { Logo: LogoApple, title: "Apple" },
  { Logo: LogoAxa, title: "AXA" },
  { Logo: LogoCisco, title: "Cisco" },
  { Logo: LogoElectrolux, title: "Electrolux" },
  { Logo: LogoForm, title: "Form" },
  { Logo: LogoFortytwoStudio, title: "FortyTwo Studio" },
  { Logo: LogoMapfre, title: "Mapfre" },
  { Logo: LogoMicrosoft, title: "Microsoft" },
  { Logo: LogoMidmark, title: "Midmark" },
  { Logo: LogoMobbex, title: "Mobbex" },
  { Logo: LogoNovartis, title: "Novartis" },
  { Logo: LogoOpticks, title: "Opticks" },
  { Logo: LogoTheWallStreetJournal, title: "The Wall Street Journal" },
  { Logo: LogoUnitedNations, title: "United Nations" },
];

const defaultReferences: Exclude<ReferencesProps["references"], undefined> = [
  "Anker",
  "Apple",
  "Microsoft",
  "Cisco",
  "United Nations",
  "The Wall Street Journal",
  "Novartis",
  "Electrolux",
  "Mapfre",
  "AXA",
];

export default function References({ className, references = defaultReferences, ...props }: ReferencesProps) {
  const [state, setState] = useState<State>({ isReady: false });

  const selectedReferences = useMemo(() => {
    const v = [];

    for (const reference of references) {
      const r = allReferences.find((d) => d.title === reference);

      if (r !== undefined) {
        v.push(r);
      }
    }

    return v;
  }, [references]);

  const body = useMemo(() => {
    const content = (
      <div className="align-items-center d-flex gap-32 justify-content-around position-relative px-16">
        {selectedReferences.map((r) => (
          <div
            className="align-items-center d-flex h-3rem text-body-tertiary text-body-secondary-focus-visible text-body-secondary-hover
            transition-all"
            key={r.title}
            title={r.title}
          >
            <r.Logo className="d-block w-100" style={{ height: `${r.heightPercentage || 100}%` }} />
          </div>
        ))}
      </div>
    );

    return state.isReady ? (
      <Marquee pauseOnHover speed={20}>{content}</Marquee>
    ) : content;
  }, [selectedReferences, state.isReady]);

  useEffect(() => {
    setState((s) => ({ ...s, isReady: true }));
  }, []);

  return (
    <div {...props} className={classNames("backdrop-blur-2px border-top py-16", className)}>
      <div className="container">
        <p className="fs-5 fw-semi-bold text-body-secondary text-center">
          WebGazer is trusted to maximize uptime and monitor performance all around the world.
        </p>

        <div className="mt-16 position-relative">
          <div className={styles.overlay} />

          {body}
        </div>
      </div>
    </div>
  );
}
